import React, { useEffect, useState } from "react";
import { LoaderCircle, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const inputCls = "h-10 w-full rounded-xl border border-[#E4DFD3] bg-white px-3.5 text-sm text-[#1A1D1A] outline-none transition-colors focus:border-[#576856]";
const areaCls = "w-full rounded-xl border border-[#E4DFD3] bg-white px-3.5 py-2.5 text-sm text-[#1A1D1A] outline-none transition-colors focus:border-[#576856]";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#6D736D]";

const GROUPS = [
    {
        key: "hero", title: "Hero",
        fields: [
            ["hero.eyebrow", "Eyebrow"],
            ["hero.title", "Title (H1)"],
            ["hero.subtitle", "Subtitle", "textarea"],
            ["hero.image", "Image URL"],
        ],
    },
    {
        key: "causes", title: "3 causes of bad breath",
        fields: [["causes.title", "Section title"], ["causes.intro", "Intro", "textarea"]],
        items: { path: "causes.items", label: "Cause", fields: [["title", "Title"], ["body", "Text", "textarea"]] },
    },
    {
        key: "bridge", title: "Common questions (bridge)",
        fields: [["bridge.title", "Section title"], ["bridge.intro", "Intro", "textarea"]],
        items: {
            path: "bridge.items", label: "Question",
            fields: [["question", "Question"], ["answer", "Answer", "textarea"], ["link_label", "Link label"], ["target", "Anchor target (e.g. #solution-1)"]],
        },
    },
    {
        key: "solutions", title: "3 solutions that actually work",
        fields: [["solutions.title", "Section title"], ["solutions.intro", "Intro", "textarea"]],
        items: { path: "solutions.items", label: "Solution", fields: [["title", "Title"], ["body", "Text", "textarea"]] },
    },
    {
        key: "products_section", title: "Products section",
        fields: [
            ["products_section.title", "Section title"],
            ["products_section.intro", "Intro", "textarea"],
            ["products_section.button_label", "Button label"],
            ["products_section.microcopy", "Microcopy under button"],
        ],
    },
    {
        key: "analyzers", title: "Analysers section",
        fields: [["analyzers.title", "Section title"], ["analyzers.intro", "Intro", "textarea"]],
        table: true,
    },
    {
        key: "faq", title: "FAQ",
        fields: [["faq.title", "Section title"], ["faq.intro", "Intro", "textarea"]],
        items: { path: "faq.items", label: "Question", fields: [["q", "Question"], ["a", "Answer", "textarea"]] },
    },
    {
        key: "email", title: "Email capture",
        fields: [
            ["email.title", "Title"],
            ["email.text", "Text", "textarea"],
            ["email.button_label", "Button label"],
            ["email.success_message", "Success message"],
        ],
    },
    {
        key: "footer", title: "Footer disclosures",
        fields: [
            ["footer.affiliate_title", "Affiliate title"],
            ["footer.affiliate_text", "Affiliate text", "textarea"],
            ["footer.medical_title", "Medical title"],
            ["footer.medical_text", "Medical text", "textarea"],
            ["footer.advertising_title", "Advertising title"],
            ["footer.advertising_text", "Advertising text", "textarea"],
        ],
    },
];

const getDeep = (obj, path) => path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

const TableEditor = ({ content, update }) => (
    <div className="space-y-3">
        <p className={labelCls}>Comparison table</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {content.analyzers.table.headers.map((h, i) => (
                <input key={i} data-testid={`content-table-header-${i}`} className={inputCls} value={h}
                    onChange={(e) => update(`analyzers.table.headers.${i}`, e.target.value)} />
            ))}
        </div>
        {content.analyzers.table.rows.map((row, ri) => (
            <div key={ri} className="space-y-2 rounded-xl border border-[#E4DFD3] bg-[#FDFCF8] p-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {row.cells.map((c, ci) => (
                        <input key={ci} data-testid={`content-table-row-${ri}-cell-${ci}`} className={inputCls} value={c}
                            onChange={(e) => update(`analyzers.table.rows.${ri}.cells.${ci}`, e.target.value)} />
                    ))}
                </div>
                <label className="flex items-center gap-2 text-xs text-[#6D736D]">
                    <input type="checkbox" className="h-4 w-4 accent-[#576856]" checked={!!row.highlighted}
                        onChange={(e) => update(`analyzers.table.rows.${ri}.highlighted`, e.target.checked)} />
                    Highlighted row
                </label>
            </div>
        ))}
    </div>
);

export const ContentTab = () => {
    const [content, setContent] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/site-content").then(({ data }) => setContent(data)).catch((e) => toast.error(formatApiError(e)));
    }, []);

    const update = (path, value) => setContent((prev) => {
        const next = JSON.parse(JSON.stringify(prev));
        const keys = path.split(".");
        let cur = next;
        for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
        cur[keys[keys.length - 1]] = value;
        return next;
    });

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/site-content", content);
            toast.success("Content saved — live on the site now.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (!content) return <div className="flex justify-center py-16"><LoaderCircle className="animate-spin text-[#718370]" /></div>;

    const renderField = (path, label, type) => (
        <div key={path}>
            <label className={labelCls}>{label}</label>
            {type === "textarea" ? (
                <textarea data-testid={`content-field-${path.replace(/\./g, "-")}`} className={areaCls} rows={3}
                    value={getDeep(content, path) ?? ""} onChange={(e) => update(path, e.target.value)} />
            ) : (
                <input data-testid={`content-field-${path.replace(/\./g, "-")}`} className={inputCls}
                    value={getDeep(content, path) ?? ""} onChange={(e) => update(path, e.target.value)} />
            )}
        </div>
    );

    return (
        <div className="space-y-4">
            <p className="rounded-xl border border-[#B89052]/30 bg-[#B89052]/10 px-4 py-3 text-xs font-medium text-[#B89052]">
                Changes go live on the public site as soon as you save.
            </p>
            {GROUPS.map((g) => (
                <details key={g.key} data-testid={`content-group-${g.key}`} open={g.key === "hero"}
                    className="rounded-2xl border border-[#E4DFD3] bg-white">
                    <summary className="cursor-pointer select-none px-5 py-4 text-sm font-bold text-[#1A1D1A]">{g.title}</summary>
                    <div className="space-y-4 border-t border-[#E4DFD3] px-5 py-5">
                        {g.fields?.map(([path, label, type]) => renderField(path, label, type))}
                        {g.items && (getDeep(content, g.items.path) || []).map((_, i) => (
                            <div key={i} className="space-y-3 rounded-xl border border-[#E4DFD3] bg-[#FDFCF8] p-4">
                                <p className="text-xs font-bold uppercase tracking-wider text-[#718370]">{g.items.label} {i + 1}</p>
                                {g.items.fields.map(([suffix, label, type]) => renderField(`${g.items.path}.${i}.${suffix}`, label, type))}
                            </div>
                        ))}
                        {g.table && <TableEditor content={content} update={update} />}
                    </div>
                </details>
            ))}
            <button data-testid="content-save-btn" onClick={save} disabled={saving}
                className="flex items-center gap-2 rounded-full bg-[#576856] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1A1D1A] disabled:opacity-70">
                {saving ? <LoaderCircle size={15} className="animate-spin" /> : <Save size={15} />}
                Save content
            </button>
        </div>
    );
};
